import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n';

import {
  fetchAuthProviders,
  signInWithGoogle,
  type ReferralPayload,
  type SocialSession,
} from '../../api/socialAuth';
import {
  GoogleSignInError,
  preloadGoogleIdentity,
  requestGoogleCode,
} from '../../lib/googleIdentity';
import { GoogleIcon, WhatsAppIcon } from './BrandIcons';
import { WhatsAppSignIn } from './WhatsAppSignIn';

type Provider = 'google' | 'whatsapp';

const PROVIDER_NAMES: Record<Provider, string> = { google: 'Google', whatsapp: 'WhatsApp' };

export interface SocialAuthProps {
  mode: 'login' | 'register';
  /** Carried into an account created on the way, as the password form does. */
  referral?: ReferralPayload;
  /**
   * The session is in hand and the token stored. The page reloads the account
   * and decides where it goes next; a throw here is shown as an error.
   */
  onAuthenticated: (session: SocialSession, provider: Provider) => Promise<void> | void;
  /** The email and password form, shown below the divider. */
  children: ReactNode;
}

/**
 * The sign-in options above the email form: Google, WhatsApp, then "or".
 *
 * Each option is offered only when the API says it is configured, so a
 * deployment without a Google client simply does not show the button - and if
 * that answer cannot be had at all, the page is exactly the email form it
 * always was. Choosing WhatsApp swaps the whole panel for its short flow, with
 * a way back; Google opens its own popup and never leaves the page.
 */
export function SocialAuth({ mode, referral, onAuthenticated, children }: SocialAuthProps) {
  const providers = useQuery({
    queryKey: ['auth', 'providers'],
    queryFn: fetchAuthProviders,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const t = useT();
  const [view, setView] = useState<'options' | 'whatsapp'>('options');
  const [busy, setBusy] = useState<Provider | null>(null);
  const [googleStage, setGoogleStage] = useState<'popup' | 'exchanging'>('popup');
  const [error, setError] = useState<{ message: string; tone: 'error' | 'info' } | null>(null);
  const [done, setDone] = useState<{ provider: Provider; isNew: boolean } | null>(null);

  const googleClientId = providers.data?.google.enabled ? providers.data.google.client_id : null;
  const whatsappEnabled = providers.data?.whatsapp.enabled ?? false;
  const captcha = providers.data?.captcha;
  // Every code is a paid message, so the API may ask for the check on the
  // very first request; showing it up front saves a refusal.
  const whatsappChallenge =
    captcha?.enabled && captcha.whatsapp_code_always && captcha.site_key
      ? { siteKey: captcha.site_key, action: 'whatsapp_code' }
      : null;
  const anyEnabled = Boolean(googleClientId) || whatsappEnabled;

  // Fetched while the visitor reads the page, so the popup opens on the click
  // itself - a popup opened after an await is one the browser may block.
  useEffect(() => {
    if (googleClientId) preloadGoogleIdentity();
  }, [googleClientId]);

  const finish = async (session: SocialSession, provider: Provider) => {
    setDone({ provider, isNew: session.is_new_account });
    try {
      await onAuthenticated(session, provider);
    } catch (caught) {
      setDone(null);
      setError({
        tone: 'error',
        message:
          caught instanceof ApiError
            ? caught.message
            : t('auth.social.load_failed'),
      });
    }
  };

  const continueWithGoogle = async () => {
    if (!googleClientId) return;
    setError(null);
    setBusy('google');
    setGoogleStage('popup');
    try {
      const code = await requestGoogleCode(googleClientId);
      setGoogleStage('exchanging');
      const session = await signInWithGoogle(code, referral);
      await finish(session, 'google');
    } catch (caught) {
      if (caught instanceof GoogleSignInError) {
        setError({
          tone: caught.reason === 'cancelled' ? 'info' : 'error',
          message: caught.message,
        });
      } else if (caught instanceof ApiError && caught.code === 'REFERRAL_CODE_INVALID') {
        setError({
          tone: 'error',
          message: t('auth.social.referral_fix', { message: caught.message }),
        });
      } else {
        setError({
          tone: 'error',
          message:
            caught instanceof ApiError
              ? caught.message
              : t('auth.social.google_failed'),
        });
      }
    } finally {
      setBusy(null);
    }
  };

  if (done) {
    return (
      <div className="py-6 text-center" role="status" aria-live="polite">
        <div className="mx-auto grid size-12 place-items-center rounded-full bg-brand-50">
          <CheckCircle2 className="size-6 text-brand-600" aria-hidden />
        </div>
        <p className="mt-4 text-lg font-semibold text-ink">
          {done.isNew
            ? t('auth.social.account_ready')
            : t('auth.social.signed_in_with', { provider: PROVIDER_NAMES[done.provider] })}
        </p>
        <p className="mt-1.5 inline-flex items-center gap-2 text-sm text-muted">
          <Spinner size="sm" />
          {done.isNew ? t('auth.social.setting_up') : t('auth.social.to_dashboard')}
        </p>
      </div>
    );
  }

  if (view === 'whatsapp') {
    return (
      <WhatsAppSignIn
        mode={mode}
        referral={referral}
        captcha={whatsappChallenge}
        onBack={() => setView('options')}
        onAuthenticated={(session) => finish(session, 'whatsapp')}
      />
    );
  }

  const withProvider = (provider: string) =>
    mode === 'register'
      ? t('auth.social.sign_up_with', { provider })
      : t('auth.social.continue_with', { provider });

  return (
    <div>
      {providers.isPending ? (
        // Held open while the options load, so the form does not jump down
        // under the cursor a moment after the page appears.
        <div className="grid grid-cols-2 gap-3" aria-hidden>
          <div className="h-12 animate-pulse rounded-xl bg-surface" />
          <div className="h-12 animate-pulse rounded-xl bg-surface" />
        </div>
      ) : anyEnabled ? (
        <div className="space-y-3">
          {error ? <Alert tone={error.tone}>{error.message}</Alert> : null}

          {/* Side by side with just the provider's name: the icon and the
              divider below already say what the buttons are for. The full
              wording stays as the accessible name. */}
          <div className={cn('grid gap-3', googleClientId && whatsappEnabled && 'grid-cols-2')}>
            {googleClientId ? (
              <ProviderButton
                icon={<GoogleIcon className="size-5" />}
                label="Google"
                ariaLabel={withProvider('Google')}
                busyLabel={googleStage === 'popup' ? t('auth.social.waiting') : t('auth.social.signing_in')}
                busy={busy === 'google'}
                disabled={busy !== null}
                onClick={() => void continueWithGoogle()}
              />
            ) : null}
            {whatsappEnabled ? (
              <ProviderButton
                icon={<WhatsAppIcon className="size-5" />}
                label="WhatsApp"
                ariaLabel={withProvider('WhatsApp')}
                busy={false}
                disabled={busy !== null}
                onClick={() => {
                  setError(null);
                  setView('whatsapp');
                }}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {providers.isPending || anyEnabled ? (
        <div className="my-6 flex items-center gap-3" role="separator" aria-label={t('auth.social.or')}>
          <span className="h-px flex-1 bg-line" />
          <span className="text-xs font-medium uppercase tracking-wider text-muted">
            {mode === 'register' ? t('auth.social.or_sign_up_email') : t('auth.social.or_email')}
          </span>
          <span className="h-px flex-1 bg-line" />
        </div>
      ) : null}

      {children}
    </div>
  );
}

function ProviderButton({
  icon,
  label,
  ariaLabel,
  busyLabel,
  busy,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  /** The full wording, for screen readers, when the visible label is short. */
  ariaLabel?: string;
  busyLabel?: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-busy={busy}
      aria-label={busy && busyLabel ? busyLabel : ariaLabel}
      className={cn(
        'relative flex h-12 w-full min-w-0 items-center justify-center gap-2.5 rounded-xl border border-line bg-white px-3',
        'text-[15px] font-semibold text-ink shadow-sm transition',
        'hover:border-brand-300 hover:bg-brand-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-200',
        'disabled:cursor-not-allowed disabled:opacity-60',
      )}
    >
      {busy ? <Spinner size="sm" className="text-muted" /> : icon}
      <span>{busy && busyLabel ? busyLabel : label}</span>
    </button>
  );
}
